import React, { useState } from "react";
import http from "../../../api/apiClient";
import { API_PATH, EVENT_TYPES, emptyForm } from "../constants";
import { createEventPayloads } from "../createEventPayloads";
import type { CalendarFormState, ModalState, SchoolCalendarEvent, SchoolYear } from "../types";
import {
  buildCalendarDateTime,
  compareDateTime,
  eventClassroomUuids,
  eventId,
  eventScope,
  getDatePart,
  getTimePart,
  normalizeModalError,
  schoolYearId,
} from "../utils";

interface UseSchoolCalendarFormArgs {
  t: any;
  canAdd: boolean;
  canEdit: boolean;
  selectedSchoolYearUuid: string;
  loadEvents: () => Promise<void>;
  showToast: (message: string, type?: "success" | "error" | "info") => void;
  setModal: React.Dispatch<React.SetStateAction<ModalState>>;
}

export const useSchoolCalendarForm = ({
  t,
  canAdd,
  canEdit,
  selectedSchoolYearUuid,
  loadEvents,
  showToast,
  setModal,
}: UseSchoolCalendarFormArgs) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<SchoolCalendarEvent | null>(null);
  const [form, setForm] = useState<CalendarFormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof CalendarFormState, string>>>({});
  const [saving, setSaving] = useState(false);

  const openCreateForm = (presetDate = "", presetEndDate = presetDate) => {
    if (!selectedSchoolYearUuid) {
      setModal({
        show: true,
        type: "warning",
        title: t("schoolCalendar.fields.schoolYear", { defaultValue: "Schooljaar" }),
        message: t("schoolCalendar.precondition", {
          defaultValue: "Kies eerst een schooljaar om de planning te beheren.",
        }),
      });
      return;
    }

    const normalizedStart = presetDate && presetEndDate && presetEndDate < presetDate ? presetEndDate : presetDate;
    const normalizedEnd = presetDate && presetEndDate && presetEndDate < presetDate ? presetDate : presetEndDate;

    setEditingEvent(null);
    setForm({
      ...emptyForm,
      schoolYearUuid: selectedSchoolYearUuid,
      startDate: normalizedStart,
      endDate: normalizedEnd || normalizedStart,
      allDay: Boolean(normalizedStart),
      startTime: "",
      endTime: "",
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const openEditForm = (event: SchoolCalendarEvent) => {
    setEditingEvent(event);
    setForm({
      scope: eventScope(event),
      classroomUuids: eventScope(event) === "CLASSROOM" ? eventClassroomUuids(event) : [],
      schoolYearUuid: String(schoolYearId(event) || ""),
      title: event?.title || "",
      description: event?.description || "",
      eventType: EVENT_TYPES.includes(event?.eventType as any) ? (event.eventType as any) : "OTHER",
      startDate: getDatePart(event?.startDate),
      startTime: getTimePart(event?.startDate),
      endDate: getDatePart(event?.endDate),
      endTime: getTimePart(event?.endDate),
      allDay: Boolean(event?.allDay),
      location: event?.location || "",
      published: Boolean(event?.published),
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const validateForm = () => {
    const nextErrors: Partial<Record<keyof CalendarFormState, string>> = {};

    if (!form.title.trim()) {
      nextErrors.title = t("schoolCalendar.validation.titleRequired", {
        defaultValue: "Titel is verplicht.",
      });
    }

    if (!form.eventType) {
      nextErrors.eventType = t("schoolCalendar.validation.eventTypeRequired", {
        defaultValue: "Type is verplicht.",
      });
    }

    if (!selectedSchoolYearUuid && !editingEvent) {
      nextErrors.schoolYearUuid = t("schoolCalendar.validation.schoolYearRequired", {
        defaultValue: "Kies eerst een schooljaar.",
      });
    }

    if (form.scope === "CLASSROOM" && form.classroomUuids.length === 0) {
      nextErrors.classroomUuids = t("schoolCalendar.validation.classroomRequired", {
        defaultValue: "Selecteer minimaal één klas.",
      });
    }

    if (!form.startDate) {
      nextErrors.startDate = t("schoolCalendar.validation.startRequired", {
        defaultValue: "Startdatum is verplicht.",
      });
    }

    if (!form.endDate) {
      nextErrors.endDate = t("schoolCalendar.validation.endRequired", {
        defaultValue: "Einddatum is verplicht.",
      });
    }

    if (!form.allDay && !form.startTime) {
      nextErrors.startTime = t("schoolCalendar.validation.startTimeRequired", {
        defaultValue: "Starttijd is verplicht.",
      });
    }

    if (!form.allDay && !form.endTime) {
      nextErrors.endTime = t("schoolCalendar.validation.endTimeRequired", {
        defaultValue: "Eindtijd is verplicht.",
      });
    }

    if (
      form.startDate &&
      form.endDate &&
      compareDateTime(form.endDate, form.endTime, form.allDay) <
        compareDateTime(form.startDate, form.startTime, form.allDay)
    ) {
      const message = form.allDay
        ? t("schoolCalendar.validation.endBeforeStart", {
            defaultValue: "Einddatum mag niet vóór startdatum liggen.",
          })
        : t("schoolCalendar.validation.endTimeBeforeStart", {
            defaultValue: "De eindtijd mag niet vóór de starttijd liggen.",
          });

      nextErrors.endDate = message;
      if (!form.allDay) {
        nextErrors.endTime = t("schoolCalendar.validation.invalidEndTime", {
          defaultValue: "Ongeldige eindtijd",
        });
      }
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = (overrides = {}) => ({
    scope: form.scope,
    classroomUuids: form.scope === "CLASSROOM" ? form.classroomUuids : [],
    schoolYearUuid: editingEvent ? form.schoolYearUuid || null : selectedSchoolYearUuid || null,
    title: form.title.trim(),
    description: form.description.trim(),
    eventType: form.eventType,
    startDate: buildCalendarDateTime(form.startDate, form.startTime, form.allDay, false),
    endDate: buildCalendarDateTime(form.endDate, form.endTime, form.allDay, true),
    allDay: Boolean(form.allDay),
    location: form.location.trim(),
    published: Boolean(form.published),
    ...overrides,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;
    if (editingEvent && !canEdit) return;
    if (!editingEvent && !canAdd) return;

    setSaving(true);

    try {
      const id = eventId(editingEvent);
      if (id) await http.put(`${API_PATH}/${id}`, buildPayload());
      else {
        const payloads = createEventPayloads(buildPayload());
        await Promise.all(payloads.map((payload) => http.post(API_PATH, payload)));
      }

      setFormOpen(false);
      await loadEvents();
      showToast(
        editingEvent
          ? t("schoolCalendar.updated", { defaultValue: "Schoolplanning-item is bijgewerkt." })
          : t("schoolCalendar.saved", { defaultValue: "Schoolplanning-item is opgeslagen." }),
        "success",
      );
    } catch (err) {
      const normalized = normalizeModalError(
        err,
        t,
        t("schoolCalendar.saveFailed", { defaultValue: "Opslaan is mislukt." }),
      );
      showToast(normalized.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const resetFormState = () => {
    setFormOpen(false);
    setEditingEvent(null);
    setFormErrors({});
  };

  return {
    formOpen,
    setFormOpen,
    editingEvent,
    setEditingEvent,
    form,
    setForm,
    formErrors,
    setFormErrors,
    saving,
    setSaving,
    openCreateForm,
    openEditForm,
    handleSubmit,
    resetFormState,
  };
};
